import { extractSpouseInfo } from './extractSpouseInfo';
import { Spouse } from '@servicetitan/financing-api-client/src/prequals-api';
import { ApplicationData } from '../application/ApplicationDataStore';

export interface PrepareSpouseDataResult {
    /** Marital status - can come from simple or detailed fields */
    isMarried?: boolean;
    /** Spouse contact (simple marriage fields only) */
    spouseContact?: string;
    /** Detailed spouse information */
    spouse?: Spouse;
}

/**
 * Prepares primary applicant spouse data for API submission
 *
 * Checks multiple data sources:
 * 1. Mailing address fields (isMarried, spouseContact, spouseInfo)
 * 2. Job details fields (isMarried, spouseContact, spouseInfo)
 *
 * Priority:
 * - Mailing address data takes precedence (for flows with hasMailingAddress: true)
 * - Job details data as fallback (for embedded flows with hasMailingAddress: false)
 *
 * @param applicationData - Full application data from store
 * @param homeAddress - Primary applicant's home address (for spouse address when same as applicant)
 * @returns Formatted spouse data ready for API payload
 */
export const preparePrimaryApplicantSpouseData = (
    applicationData: ApplicationData,
    homeAddress: any,
): PrepareSpouseDataResult => {
    const primaryApplicantData = applicationData.applicant.primaryApplicant;
    const mailingAddressData = applicationData.mailingAddress.primaryApplicant;
    const jobDetailsData = primaryApplicantData?.jobDetails;

    // Priority: mailing address spouseInfo > job details spouseInfo
    let spouse = extractSpouseInfo({
        spouseInfo: mailingAddressData?.spouseInfo,
        isMarried: mailingAddressData?.isMarried,
        applicantAddress: homeAddress,
    });

    spouse ??= extractSpouseInfo({
        spouseInfo: jobDetailsData?.spouseInfo,
        isMarried: jobDetailsData?.isMarried,
        applicantAddress: homeAddress,
    });

    const isMarried =
        spouse?.isMarried ?? mailingAddressData?.isMarried ?? jobDetailsData?.isMarried;

    // spouseContact only exists in simple marriage fields
    // Check mailing address first, then job details (for ServiceFinanceEmbedded)
    const spouseContact = mailingAddressData?.spouseContact ?? jobDetailsData?.spouseContact;

    // Simple marriage fields store spouse names as flat fields alongside spouseContact
    if (!spouse && isMarried === true) {
        const firstName = mailingAddressData?.spouseFirstName ?? jobDetailsData?.spouseFirstName;
        const lastName = mailingAddressData?.spouseLastName ?? jobDetailsData?.spouseLastName;
        if (firstName || lastName) {
            spouse = { isMarried: true, firstName, lastName };
        }
    }

    return {
        isMarried,
        spouseContact,
        spouse,
    };
};

/**
 * Prepares co-applicant spouse data for API submission
 *
 * Checks multiple data sources in priority order:
 * 1. Co-applicant form fields (isMarried, spouseContact, spouseInfo)
 * 2. Job details fields (isMarried, spouseContact, spouseInfo)
 *
 * Note: Co-applicant data is stored in coApplicant.coApplicant, not mailingAddress.coApplicant
 *
 * @param applicationData - Full application data from store
 * @param coApplicantJobDetails - Co-applicant job details (if available)
 * @param coApplicantAddress - Co-applicant's address (for spouse address when same as applicant)
 * @returns Formatted spouse data ready for API payload
 */
export const prepareCoApplicantSpouseData = (
    applicationData: ApplicationData,
    coApplicantJobDetails: any,
    coApplicantAddress: any,
): PrepareSpouseDataResult => {
    // Co-applicant data is stored in coApplicant.coApplicant
    const coApplicantData = (applicationData.coApplicant as any)?.coApplicant;

    let spouse = extractSpouseInfo({
        spouseInfo: coApplicantData?.spouseInfo,
        isMarried: coApplicantData?.isMarried,
        applicantAddress: coApplicantAddress,
    });

    if (!spouse && coApplicantJobDetails?.spouseInfo) {
        spouse = extractSpouseInfo({
            spouseInfo: coApplicantJobDetails.spouseInfo,
            isMarried: coApplicantJobDetails?.isMarried,
            applicantAddress: coApplicantAddress,
        });
    }

    const isMarried =
        spouse?.isMarried ?? coApplicantData?.isMarried ?? coApplicantJobDetails?.isMarried;

    // spouseContact only exists in simple fields
    // Check co-applicant data first, then job details (for embedded flows)
    const spouseContact = coApplicantData?.spouseContact ?? coApplicantJobDetails?.spouseContact;

    // For simple marriage fields, spouse names are stored separately
    if (!spouse && isMarried === true) {
        const firstName =
            coApplicantData?.spouseFirstName ?? coApplicantJobDetails?.spouseFirstName;
        const lastName = coApplicantData?.spouseLastName ?? coApplicantJobDetails?.spouseLastName;
        if (firstName || lastName) {
            spouse = { isMarried: true, firstName, lastName };
        }
    }

    return {
        isMarried,
        spouseContact,
        spouse,
    };
};
